import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bookings, isApiError } from '@repletoryreach/sdk-api';
import { Button, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifyError, notifyInfo, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useT, type Translate } from '../../../i18n/I18nProvider';
import { BOOKINGS_KEY } from '../lib/data';

// Calendar sync (8 October 2026): each bookable person, or the business, connects a Google or Microsoft calendar.

const PROVIDERS: bookings.CalendarProvider[] = ['google', 'microsoft'];
const CALENDAR_CODES = [
  bookings.CALENDAR_SYNC_UNAVAILABLE, bookings.CALENDAR_PROVIDER_NOT_CONFIGURED, bookings.CALENDAR_LINK_EXPIRED,
  bookings.CALENDAR_NOT_YOURS, bookings.CALENDAR_REFUSED, 'PLAN_LIMIT_REACHED'
];

export function useCalendars() {
  return useQuery({ queryKey: [BOOKINGS_KEY, 'calendars'], queryFn: () => bookings.getCalendars(), staleTime: 30_000 });
}

function calendarErrorText(t: Translate, e: unknown): string {
  if (isApiError(e) && CALENDAR_CODES.includes(e.code)) return t(`bookings.calendar.error.${e.code}`);
  if (isApiError(e) && e.status === 403) return t('bookings.error.forbidden');
  return t('bookings.calendar.error.generic');
}

/** Finishes a connection when Google or Microsoft sends the person back here, then tidies the address bar. */
export function useCalendarReturn() {
  const t = useT();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const done = useRef(false);
  useEffect(() => {
    const outcome = params.get('calendar');
    if (!outcome || done.current) return;
    done.current = true;
    const provider = params.get('provider') as bookings.CalendarProvider | null;
    const code = params.get('code');
    const state = params.get('state');
    nav('/bookings/staff', { replace: true });
    if (outcome === 'return' && provider && code && state) {
      bookings.completeCalendar(provider, code, state)
        .then((r) => {
          void qc.invalidateQueries({ queryKey: [BOOKINGS_KEY, 'calendars'] });
          notifySuccess(t('bookings.calendar.toast.connected', { account: r.data.account_email ?? t(`bookings.calendar.provider.${provider}`) }), t('bookings.calendar.sync.title'));
        })
        .catch((e) => notifyError(calendarErrorText(t, e), t('bookings.calendar.sync.title')));
    } else if (outcome === 'cancelled') notifyInfo(t('bookings.calendar.toast.cancelled'), t('bookings.calendar.sync.title'));
    else notifyError(t('bookings.calendar.error.generic'), t('bookings.calendar.sync.title'));
  }, [params, nav, qc, t]);
}

function useCalendarActions() {
  const t = useT();
  const qc = useQueryClient();
  const connect = useMutation({
    mutationFn: (v: { provider: bookings.CalendarProvider; staffId: string | null }) => bookings.connectCalendar(v.provider, v.staffId),
    onSuccess: (r) => { window.location.assign(r.data.url); },
    onError: (e) => notifyError(calendarErrorText(t, e), t('bookings.calendar.sync.title'))
  });
  const disconnect = useMutation({
    mutationFn: (c: bookings.CalendarConnection) => bookings.disconnectCalendar(c.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [BOOKINGS_KEY, 'calendars'] });
      notifySuccess(t('bookings.calendar.toast.disconnected'), t('bookings.calendar.sync.title'));
    },
    onError: (e) => notifyError(calendarErrorText(t, e), t('bookings.calendar.sync.title'))
  });
  function confirmDisconnect(c: bookings.CalendarConnection) {
    openConfirmModal({
      title: t('bookings.calendar.disconnect.title'),
      children: <Text role="bodySmall">{t('bookings.calendar.disconnect.body')}</Text>,
      labels: { confirm: t('bookings.calendar.disconnect'), cancel: t('action.back') },
      confirmProps: { color: 'red' },
      onConfirm: () => disconnect.mutate(c)
    });
  }
  return { connect, disconnect, confirmDisconnect };
}

/** One calendar's state and buttons: for a person (staffId) or the business (null). */
export function CalendarCell({ overview, staffId, canEdit, compact = false }: {
  overview: bookings.CalendarOverview | undefined; staffId: string | null; canEdit: boolean; compact?: boolean;
}) {
  const t = useT();
  const { connect, confirmDisconnect, disconnect } = useCalendarActions();
  if (!overview) return null;
  const c = overview.connections.find((x) => x.staff_id === staffId);
  if (c) {
    const broken = c.status === 'error';
    return (
      <Group gap="xs" wrap="nowrap" onClick={(e) => e.stopPropagation()}>
        <StatusBadge status={broken ? 'down' : 'healthy'}>
          {broken ? t('bookings.calendar.status.error') : t(`bookings.calendar.provider.${c.provider}`)}
        </StatusBadge>
        {!compact && c.account_email && <Text role="caption" c="var(--rr-color-text-muted)">{c.account_email}</Text>}
        {canEdit && broken && (
          <Button size="xs" variant="subtle" loading={connect.isPending} onClick={() => connect.mutate({ provider: c.provider, staffId })}>
            {t('bookings.calendar.reconnect')}
          </Button>
        )}
        {canEdit && (
          <Button size="xs" variant="subtle" color="red" loading={disconnect.isPending && disconnect.variables?.id === c.id} onClick={() => confirmDisconnect(c)}>
            {t('bookings.calendar.disconnect')}
          </Button>
        )}
      </Group>
    );
  }
  if (!canEdit || !overview.allowed) return <Text role="caption" c="var(--rr-color-text-muted)">{t('bookings.calendar.none')}</Text>;
  return (
    <Group gap="xs" wrap="nowrap" onClick={(e) => e.stopPropagation()}>
      {PROVIDERS.filter((p) => overview.available[p]).map((p) => (
        <Button key={p} size="xs" variant="default" loading={connect.isPending && connect.variables?.provider === p && connect.variables?.staffId === staffId}
          onClick={() => connect.mutate({ provider: p, staffId })}>
          {t(`bookings.calendar.connect.${p}`)}
        </Button>
      ))}
      {PROVIDERS.every((p) => !overview.available[p]) && <Text role="caption" c="var(--rr-color-text-muted)">{t('bookings.calendar.notYet')}</Text>}
    </Group>
  );
}

/** The business's own calendar, with what sync does, above the people. */
export function CalendarPanel({ canEdit }: { canEdit: boolean }) {
  const t = useT();
  const nav = useNavigate();
  const q = useCalendars();
  const overview = q.data?.data;
  if (!overview) return null;
  return (
    <Stack gap="xs" className="rr-bookings__calendar-panel" data-guide="bookings-calendar-sync">
      <Text role="bodySmall" fw={600}>{t('bookings.calendar.sync.title')}</Text>
      <Text role="caption" c="var(--rr-color-text-muted)">{t('bookings.calendar.sync.description')}</Text>
      {!overview.allowed ? (
        <Alert color="info">
          <Group gap="sm" justify="space-between" wrap="wrap">
            <span>{t('bookings.calendar.notOnPlan')}</span>
            <Button size="xs" variant="default" onClick={() => nav('/billing')}>{t('bookings.limit.seePlans')}</Button>
          </Group>
        </Alert>
      ) : (
        <Group gap="sm" wrap="wrap">
          <Text role="bodySmall">{t('bookings.calendar.business')}</Text>
          <CalendarCell overview={overview} staffId={null} canEdit={canEdit} />
        </Group>
      )}
    </Stack>
  );
}
